import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, X, Loader, Trash2, UserPlus, Search,
  GraduationCap, Users, ChevronRight, Sparkles, Clock, Settings,
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/formatters';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── Posh visual primitives ──────────────────────────────────────────────────

function GlowCard({ color, style, children, onClick, className = '' }) {
  return (
    <div
      onClick={onClick}
      className={`app-card rounded-2xl ${className}`}
      style={{
        background: `linear-gradient(135deg, ${color}14 0%, ${color}04 100%)`,
        border: `1px solid ${color}30`,
        ...style,
      }}>
      {children}
    </div>
  );
}

function IconBadge({ icon: Icon, color, size = 34, iconSize = 16 }) {
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: size >= 36 ? 13 : 11,
        background: `linear-gradient(135deg, ${color}25, ${color}15)`,
        border: `1.5px solid ${color}45`,
      }}>
      <Icon size={iconSize} color={color} />
    </div>
  );
}

const STATUS_COLORS = {
  Excellent:    '#16C784',
  Good:         '#3b82f6',
  'Needs Focus': '#f59e0b',
  'At Risk':    '#ef4444',
};

function StatusChip({ status }) {
  const color = STATUS_COLORS[status] || '#6b7280';
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
      style={{ background: `${color}18`, color, border: `1px solid ${color}35` }}>
      {status}
    </span>
  );
}

function PendingChip({ status, colors }) {
  const map = {
    pending:  { bg: 'rgba(245,158,11,0.14)', color: '#f59e0b', label: 'Pending' },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}30` }}>
      {s.label}
    </span>
  );
}

export default function MentorshipPage() {
  const { colors } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const myId = user?.user_id;
  const ac = colors.accent;

  const [isMentor, setIsMentor]   = useState(false);
  const [toggling, setToggling]   = useState(false);
  const [tab, setTab]             = useState('students'); // 'mentors' | 'students'
  const [mentors, setMentors]     = useState([]);
  const [students, setStudents]   = useState([]);
  const [cohort, setCohort]       = useState([]);
  const [overview, setOverview]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const [showAdd, setShowAdd]     = useState(false);
  const [addAsRole, setAddAsRole] = useState('mentor');
  const [email, setEmail]         = useState('');
  const [sending, setSending]     = useState(false);
  const [addError, setAddError]   = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, mentorsRes, studentsRes] = await Promise.allSettled([
        axios.get(`${API}/mentorship/me`),
        axios.get(`${API}/mentorship/mentors`),
        axios.get(`${API}/mentorship/students`),
      ]);
      if (meRes.status === 'fulfilled') setIsMentor(!!meRes.value.data.is_mentor);
      if (mentorsRes.status === 'fulfilled') setMentors(mentorsRes.value.data);
      if (studentsRes.status === 'fulfilled') setStudents(studentsRes.value.data);
    } finally { setLoading(false); }
  }, []);

  const loadCohort = useCallback(async () => {
    try {
      const [cohortRes, overviewRes] = await Promise.allSettled([
        axios.get(`${API}/mentorship/students/cohort`),
        axios.get(`${API}/mentorship/students/overview`),
      ]);
      if (cohortRes.status === 'fulfilled') setCohort(cohortRes.value.data.students || []);
      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === 'students') loadCohort(); }, [tab, loadCohort]);

  useEffect(() => {
    if (!loading && students.length === 0 && mentors.length > 0) setTab('mentors');
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live user search inside the invite modal — debounced, cancels stale requests
  useEffect(() => {
    if (!showAdd || selectedUser || email.trim().length < 3) { setSearchResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/mentorship/search`, { params: { q: email.trim() } });
        if (!cancelled) setSearchResults(data);
      } catch { if (!cancelled) setSearchResults([]); }
      finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [email, showAdd, selectedUser]);

  const handleToggleMentor = async () => {
    setToggling(true);
    try {
      const { data } = await axios.post(`${API}/mentorship/toggle-mentor`);
      setIsMentor(data.is_mentor);
      if (data.is_mentor) loadCohort();
    } catch { import('react-hot-toast').then(m => m.default.error('Failed to update mentor status')); }
    finally { setToggling(false); }
  };

  const handleSendRequest = async () => {
    const targetEmail = selectedUser?.email || email.trim();
    if (!targetEmail) { setAddError('Search for and select a person to invite'); return; }
    setAddError(''); setAddSuccess(''); setSending(true);
    try {
      await axios.post(`${API}/mentorship/requests`, { email: targetEmail, as_role: addAsRole });
      setAddSuccess(`Request sent to ${selectedUser?.full_name || targetEmail}!`);
      setEmail(''); setSelectedUser(null); setSearchResults([]);
      loadAll();
    } catch (err) {
      setAddError(err?.response?.data?.detail || 'Failed to send request');
    } finally { setSending(false); }
  };

  const closeAddModal = () => {
    setShowAdd(false); setEmail(''); setSelectedUser(null); setSearchResults([]); setAddError(''); setAddSuccess('');
  };

  const handleAccept = async (id) => {
    try { await axios.post(`${API}/mentorship/requests/${id}/accept`); loadAll(); loadCohort(); }
    catch { import('react-hot-toast').then(m => m.default.error('Failed to accept')); }
  };

  const handleDecline = async (id) => {
    try { await axios.post(`${API}/mentorship/requests/${id}/decline`); loadAll(); }
    catch { import('react-hot-toast').then(m => m.default.error('Failed to decline')); }
  };

  const handleRevoke = async (id) => {
    try { await axios.delete(`${API}/mentorship/relationships/${id}`); loadAll(); loadCohort(); }
    catch { import('react-hot-toast').then(m => m.default.error('Failed to remove')); }
  };

  const inputStyle = {
    background: colors.inputBg || '#0f0f0f',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    color: colors.primaryText,
    fontSize: 13,
    padding: '10px 12px',
    width: '100%',
    outline: 'none',
  };

  const pendingIncomingCount =
    mentors.filter(m => m.status === 'pending' && m.requested_by !== myId).length +
    students.filter(s => s.status === 'pending' && s.requested_by !== myId).length;

  const filteredStudents = students.filter(s =>
    !search.trim() || s.mentee_name?.toLowerCase().includes(search.toLowerCase()) || s.mentee_email?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredMentors = mentors.filter(m =>
    !search.trim() || m.mentor_name?.toLowerCase().includes(search.toLowerCase()) || m.mentor_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-bold" style={{ color: colors.primaryText }}>Mentorship</h1>
          <p className="text-[13px]" style={{ color: colors.secondaryText }}>Learn. Guide. Grow together.</p>
        </div>
        <button onClick={() => setShowSettings(true)} className="app-card p-2 rounded-xl relative flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${ac}12 0%, ${ac}04 100%)` }}>
          <Settings className="w-5 h-5" style={{ color: colors.primaryText }} />
          {pendingIncomingCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
              style={{ background: '#ef4444' }}>
              {pendingIncomingCount}
            </span>
          )}
        </button>
      </div>

      {/* Hero cards — also act as the Students/Mentors view switcher */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlowCard color={'#8b5cf6'} style={{ padding: 20, cursor: 'pointer', outline: tab === 'mentors' ? '2px solid #8b5cf6' : 'none' }} onClick={() => setTab('mentors')}>
          <p className="text-[16px] font-bold mb-1" style={{ color: '#a78bfa' }}>I'm a Mentee</p>
          <p className="text-[13px] mb-4" style={{ color: colors.secondaryText }}>Learn from experienced traders</p>
          <button
            onClick={(e) => { e.stopPropagation(); setTab('mentors'); setShowAdd(true); setAddAsRole('mentor'); }}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 2px 12px #8b5cf640' }}>
            Find a Mentor
          </button>
        </GlowCard>
        <GlowCard color={ac} style={{ padding: 20, cursor: 'pointer', outline: tab === 'students' ? `2px solid ${ac}` : 'none' }} onClick={() => setTab('students')}>
          <p className="text-[16px] font-bold mb-1" style={{ color: ac }}>I'm a Mentor</p>
          <p className="text-[13px] mb-4" style={{ color: colors.secondaryText }}>Guide and grow the next generation of traders</p>
          <button
            onClick={(e) => { e.stopPropagation(); setTab('students'); setShowAdd(true); setAddAsRole('mentee'); }}
            className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white"
            style={{ background: `linear-gradient(135deg, ${ac}, #6366f1)`, boxShadow: `0 2px 12px ${ac}40` }}>
            Invite a Student
          </button>
        </GlowCard>
      </div>

      {/* Overview stat strip (mentor mode) */}
      {tab === 'students' && overview && overview.total_students > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: colors.secondaryText }}>Overview</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { label: 'Total Students', value: overview.total_students, color: colors.primaryText },
              { label: 'Avg P&L', value: `${overview.avg_pnl_pct >= 0 ? '+' : ''}${overview.avg_pnl_pct}%`, color: overview.avg_pnl_pct >= 0 ? '#16C784' : '#ef4444' },
              { label: 'Avg Discipline', value: `${overview.avg_discipline_score}/100`, color: ac },
              { label: 'At Risk', value: overview.students_at_risk, color: overview.students_at_risk > 0 ? '#ef4444' : colors.primaryText },
            ].map(({ label, value, color }) => (
              <GlowCard key={label} color={color === colors.primaryText ? ac : color} style={{ padding: 12 }}>
                <p className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</p>
                <p className="text-[20px] font-bold mt-0.5" style={{ color }}>{value}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      )}

      {/* Section header + filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <p className="text-[13px] font-semibold flex items-center gap-1.5 flex-shrink-0" style={{ color: colors.primaryText }}>
          {tab === 'students' ? <><GraduationCap className="w-4 h-4" style={{ color: ac }} /> My Students</> : <><Users className="w-4 h-4" style={{ color: ac }} /> My Mentors</>}
        </p>
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.secondaryText }} />
          <input
            type="text" placeholder={tab === 'students' ? 'Filter your students...' : 'Filter your mentors...'}
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl text-[13px]"
            style={{ background: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader className="w-5 h-5 animate-spin" style={{ color: colors.secondaryText }} /></div>
      ) : tab === 'students' ? (
        filteredStudents.length === 0 ? (
          <GlowCard color={ac} style={{ padding: 32, textAlign: 'center' }}>
            <Sparkles className="w-8 h-8 mx-auto mb-2" style={{ color: ac }} />
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>No students yet — invite one to get started</p>
          </GlowCard>
        ) : (
          <>
            {/* Desktop/tablet: table */}
            <GlowCard color={ac} style={{ padding: 0, overflow: 'hidden' }} className="hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      {['Student', 'Discipline Score', 'P&L', 'Win Rate', 'Status', ''].map(h => (
                        <th key={h} className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(s => {
                      const cohortRow = cohort.find(c => c.student_id === s.mentee_id);
                      const isIncoming = s.status === 'pending' && s.requested_by !== myId;
                      const isOutgoing = s.status === 'pending' && s.requested_by === myId;
                      return (
                        <tr key={s.id}
                          onClick={() => s.status === 'accepted' && navigate(`/mentorship/students/${s.mentee_id}`, { state: { relationshipId: s.id, fullName: s.mentee_name } })}
                          style={{ borderBottom: `1px solid ${colors.border}`, cursor: s.status === 'accepted' ? 'pointer' : 'default' }}
                          className="transition-colors hover:bg-white/[0.02]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <IconBadge icon={GraduationCap} color={ac} size={30} iconSize={14} />
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{s.mentee_name}</p>
                                <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{s.mentee_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[13px] font-bold" style={{ color: colors.primaryText }}>
                            {cohortRow ? `${cohortRow.discipline_score}/100` : '—'}
                          </td>
                          <td className="px-4 py-3 text-[13px] font-bold" style={{ color: cohortRow && cohortRow.total_pnl >= 0 ? '#16C784' : cohortRow ? '#ef4444' : colors.secondaryText }}>
                            {cohortRow ? `${cohortRow.total_pnl >= 0 ? '+' : ''}${formatPrice(cohortRow.total_pnl)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-[13px] font-bold" style={{ color: colors.primaryText }}>
                            {cohortRow ? `${cohortRow.win_rate}%` : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {cohortRow ? <StatusChip status={cohortRow.status} /> : isOutgoing ? (
                              <span className="text-[11px] flex items-center gap-1" style={{ color: colors.secondaryText }}>
                                <Clock className="w-3 h-3" /> Waiting
                              </span>
                            ) : <PendingChip status={s.status} colors={colors} />}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isIncoming ? (
                              <div className="flex gap-1.5 justify-end">
                                <button onClick={(e) => { e.stopPropagation(); handleAccept(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(22,199,132,0.14)', border: '1px solid rgba(22,199,132,0.3)' }}>
                                  <Check className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleDecline(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                                  <X className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                                </button>
                              </div>
                            ) : s.status === 'accepted' ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <button onClick={(e) => { e.stopPropagation(); handleRevoke(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)' }}>
                                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                                </button>
                                <ChevronRight className="w-4 h-4" style={{ color: colors.secondaryText }} />
                              </div>
                            ) : (
                              <button onClick={(e) => { e.stopPropagation(); handleRevoke(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                                <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </GlowCard>

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
              {filteredStudents.map(s => {
                const cohortRow = cohort.find(c => c.student_id === s.mentee_id);
                const isIncoming = s.status === 'pending' && s.requested_by !== myId;
                const isOutgoing = s.status === 'pending' && s.requested_by === myId;
                return (
                  <GlowCard key={s.id} color={ac}
                    style={{ padding: '10px 12px', cursor: s.status === 'accepted' ? 'pointer' : 'default' }}
                    onClick={() => s.status === 'accepted' && navigate(`/mentorship/students/${s.mentee_id}`, { state: { relationshipId: s.id, fullName: s.mentee_name } })}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <IconBadge icon={GraduationCap} color={ac} size={30} iconSize={14} />
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{s.mentee_name}</p>
                          <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{s.mentee_email}</p>
                        </div>
                      </div>
                      {cohortRow ? <StatusChip status={cohortRow.status} /> : isOutgoing ? (
                        <span className="text-[11px] flex items-center gap-1 flex-shrink-0 ml-2" style={{ color: colors.secondaryText }}>
                          <Clock className="w-3 h-3" /> Waiting
                        </span>
                      ) : <PendingChip status={s.status} colors={colors} />}
                    </div>
                    {cohortRow && (
                      <div className="flex items-center gap-3 mb-2 text-[12px]">
                        <span style={{ color: colors.secondaryText }}>Score: <b style={{ color: colors.primaryText }}>{cohortRow.discipline_score}/100</b></span>
                        <span style={{ color: colors.secondaryText }}>WR: <b style={{ color: colors.primaryText }}>{cohortRow.win_rate}%</b></span>
                        <span style={{ color: cohortRow.total_pnl >= 0 ? '#16C784' : '#ef4444' }}>
                          <b>{cohortRow.total_pnl >= 0 ? '+' : ''}{formatPrice(cohortRow.total_pnl)}</b>
                        </span>
                      </div>
                    )}
                    <div className="flex justify-end">
                      {isIncoming ? (
                        <div className="flex gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); handleAccept(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(22,199,132,0.14)', border: '1px solid rgba(22,199,132,0.3)' }}>
                            <Check className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDecline(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                            <X className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                          </button>
                        </div>
                      ) : s.status === 'accepted' ? (
                        <div className="flex items-center gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); handleRevoke(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)' }}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                          </button>
                          <ChevronRight className="w-4 h-4" style={{ color: colors.secondaryText }} />
                        </div>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); handleRevoke(s.id); }} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                          <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                        </button>
                      )}
                    </div>
                  </GlowCard>
                );
              })}
            </div>
          </>
        )
      ) : (
        filteredMentors.length === 0 ? (
          <GlowCard color={ac} style={{ padding: 32, textAlign: 'center' }}>
            <Sparkles className="w-8 h-8 mx-auto mb-2" style={{ color: ac }} />
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>No mentors yet — find one to get started</p>
          </GlowCard>
        ) : (
          <div className="space-y-2">
            {filteredMentors.map(m => {
              const isIncoming = m.status === 'pending' && m.requested_by !== myId;
              const isOutgoing = m.status === 'pending' && m.requested_by === myId;
              return (
                <GlowCard key={m.id} color={ac} style={{ padding: '10px 14px' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <IconBadge icon={Users} color={ac} size={32} iconSize={14} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{m.mentor_name}</p>
                          <PendingChip status={m.status} colors={colors} />
                        </div>
                        <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{m.mentor_email}</p>
                        {isOutgoing && (
                          <p className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: colors.secondaryText }}>
                            <Clock className="w-3 h-3" /> Waiting for them to accept
                          </p>
                        )}
                      </div>
                    </div>
                    {isIncoming ? (
                      <div className="flex gap-1.5 flex-shrink-0 ml-2">
                        <button onClick={() => handleAccept(m.id)} className="p-1.5 rounded-lg" style={{ background: 'rgba(22,199,132,0.14)', border: '1px solid rgba(22,199,132,0.3)' }}>
                          <Check className="w-3.5 h-3.5" style={{ color: '#16C784' }} />
                        </button>
                        <button onClick={() => handleDecline(m.id)} className="p-1.5 rounded-lg" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                          <X className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => handleRevoke(m.id)} className="p-1.5 rounded-lg flex-shrink-0 ml-2"
                        style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}>
                        <Trash2 className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />
                      </button>
                    )}
                  </div>
                </GlowCard>
              );
            })}
          </div>
        )
      )}

      {/* Feature strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        {[
          { icon: Users, title: 'Full Visibility', desc: 'See complete trade history, metrics & behavior score', color: ac },
          { icon: GraduationCap, title: 'Give Guidance', desc: 'Comment on trades & share feedback', color: '#8b5cf6' },
          { icon: Sparkles, title: 'Track Progress', desc: 'Monitor performance & growth over time', color: '#16C784' },
          { icon: UserPlus, title: 'Build Better Traders', desc: 'Help mentees become consistent & disciplined', color: '#f59e0b' },
        ].map(({ icon: Icon, title, desc, color }) => (
          <div key={title} className="flex items-start gap-2.5">
            <IconBadge icon={Icon} color={color} size={32} iconSize={15} />
            <div>
              <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{title}</p>
              <p className="text-[11px]" style={{ color: colors.secondaryText }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Settings drawer */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: colors.overlayBg, backdropFilter: 'blur(2px)' }}
          onClick={() => setShowSettings(false)}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden"
            style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, boxShadow: '0 -24px 60px rgba(0,0,0,0.5), 0 8px 40px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}>
            <div className="h-1" style={{ background: `linear-gradient(90deg, ${ac}, #8b5cf6, #06b6d4)` }} />
            <div className="p-4 space-y-3">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Mentorship Settings</p>
              <div className="rounded-xl p-3 flex items-center justify-between"
                style={{ background: `linear-gradient(135deg, ${ac}12 0%, ${ac}04 100%)`, border: `1px solid ${colors.border}` }}>
                <div className="flex items-center gap-2.5">
                  <IconBadge icon={GraduationCap} color={ac} size={30} iconSize={14} />
                  <div>
                    <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>I'm a mentor</p>
                    <p className="text-[11px]" style={{ color: colors.secondaryText }}>Let students find and add you</p>
                  </div>
                </div>
                <button
                  onClick={handleToggleMentor}
                  disabled={toggling}
                  className="rounded-full relative transition-colors flex-shrink-0"
                  style={{ background: isMentor ? ac : colors.border, width: 40, height: 22 }}
                >
                  <span className="absolute rounded-full bg-white transition-transform"
                    style={{ width: 18, height: 18, left: isMentor ? 20 : 2, top: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                </button>
              </div>
              <button onClick={() => setShowSettings(false)}
                className="w-full py-2.5 rounded-xl text-[14px] font-medium"
                style={{ backgroundColor: colors.background, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add request modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: colors.overlayBg, backdropFilter: 'blur(2px)' }}
          onClick={closeAddModal}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden"
            style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, boxShadow: '0 -24px 60px rgba(0,0,0,0.5), 0 8px 40px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}>
            <div className="h-1" style={{ background: `linear-gradient(90deg, ${ac}, #8b5cf6, #06b6d4)` }} />
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2.5">
                <IconBadge icon={UserPlus} color={ac} size={32} iconSize={15} />
                <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>
                  {addAsRole === 'mentor' ? 'Add a Mentor' : 'Invite a Student'}
                </p>
              </div>
              <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                Search by name or email for the person you want to {addAsRole === 'mentor' ? 'add as your mentor' : 'invite as your student'}. They'll need to accept.
              </p>

              {selectedUser ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg"
                  style={{ background: `${ac}12`, border: `1px solid ${ac}30` }}>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{selectedUser.full_name}</p>
                    <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>
                      {selectedUser.email}{selectedUser.is_mentor ? ' · Mentor' : ''}
                    </p>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setEmail(''); }} className="p-1 rounded-md flex-shrink-0 ml-2"
                    style={{ color: colors.secondaryText }}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search name or email..."
                    value={email}
                    onChange={e => { setEmail(e.target.value); setAddError(''); setAddSuccess(''); }}
                    style={inputStyle}
                  />
                  {(email.trim().length >= 3) && (
                    <div className="mt-1.5 rounded-lg overflow-hidden max-h-52 overflow-y-auto"
                      style={{ background: colors.inputBg, border: `1px solid ${colors.border}` }}>
                      {searching ? (
                        <div className="flex justify-center py-3"><Loader className="w-4 h-4 animate-spin" style={{ color: colors.secondaryText }} /></div>
                      ) : searchResults.length === 0 ? (
                        <p className="text-[12px] text-center py-3" style={{ color: colors.secondaryText }}>No users found matching "{email.trim()}"</p>
                      ) : (
                        searchResults.map(u => (
                          <button key={u.user_id} onClick={() => { setSelectedUser(u); setSearchResults([]); }}
                            className="w-full flex items-center justify-between px-3 py-2 text-left transition-colors hover:bg-white/[0.04]"
                            style={{ borderBottom: `1px solid ${colors.border}` }}>
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{u.full_name}</p>
                              <p className="text-[11px] truncate" style={{ color: colors.secondaryText }}>{u.email}</p>
                            </div>
                            {u.is_mentor && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ml-2"
                                style={{ background: `${ac}18`, color: ac }}>
                                Mentor
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {addError && <p className="text-[12px]" style={{ color: '#ef4444' }}>{addError}</p>}
              {addSuccess && <p className="text-[12px]" style={{ color: '#16C784' }}>{addSuccess}</p>}
              <div className="flex gap-2">
                <button onClick={closeAddModal}
                  className="flex-1 py-2.5 rounded-xl text-[14px] font-medium"
                  style={{ backgroundColor: colors.background, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
                  Cancel
                </button>
                <button onClick={handleSendRequest} disabled={sending || !selectedUser}
                  className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${ac}, #8b5cf6)`, color: '#fff', boxShadow: `0 2px 12px ${ac}40`, opacity: (!selectedUser || sending) ? 0.5 : 1 }}>
                  {sending ? <Loader className="w-4 h-4 animate-spin" /> : <>Send</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
